// «Не совсем понятно» у разбора задачи и у разбора темы (docs/24-unclear.md).
//
// **Одна тихая иконка на весь разбор, а не кнопка у каждого шага** (29
// сентября 2026). Разбор читают, а не отмечают, и двенадцать слов «неясно» в
// колонку громче самих шагов. По нажатию — окошко под разбором: номера шагов
// (или разделы темы), по желанию комментарий, «Отправить». Причин на выбор
// нет: классифицировать непонимание просили того, кто как раз не понял.
//
// **Не шкала «понятно ли объяснено».** «Средне» не говорит, что чинить;
// отмеченный шаг — говорит. Насколько разбор помог, считается по следующей
// задаче того же шаблона, а не спрашивается.

import { useState } from "react";
import { COMMENT_MAX } from "../../convex/lib/unclear";
import { reasonOf } from "../lib/errors";
import { IconUnclear } from "./Icons";

export const UNCLEAR_TITLE = "Не совсем понятно";

/** Круглая кнопка-иконка в углу разбора. Подпись — во всплывающей подсказке. */
export function ToolButton({
  title,
  pressed,
  onClick,
  children,
}: {
  title: string;
  pressed?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="tool-btn"
      title={title}
      aria-label={title}
      aria-pressed={pressed}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function UnclearButton({
  open,
  marked,
  onClick,
}: {
  open: boolean;
  /** Отметка уже стоит — иконка чуть заметнее, чтобы её можно было снять. */
  marked: boolean;
  onClick: () => void;
}) {
  return (
    <ToolButton title={UNCLEAR_TITLE} pressed={open || marked} onClick={onClick}>
      <IconUnclear size={18} />
    </ToolButton>
  );
}

/**
 * Окошко отметки: какие места непонятны и комментарий. Сохраняет набор
 * целиком; пустой набор снимает отметку. Открывать только после того, как
 * отметка пришла с сервера: `saved` читается один раз, при открытии.
 */
export function UnclearPanel<K extends string | number>({
  prompt,
  options,
  saved,
  savedComment,
  onSave,
  onClose,
}: {
  prompt: string;
  options: { key: K; label: string }[];
  saved: K[];
  savedComment: string | null;
  onSave: (keys: K[], comment: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<K[]>(saved);
  const [comment, setComment] = useState(savedComment ?? "");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const toggle = (k: K) =>
    setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const had = saved.length > 0;
  const submit = (keys: K[], text: string) => {
    setBusy(true);
    setFailed(null);
    onSave(keys, text)
      .then(onClose)
      .catch((e: unknown) => setFailed(reasonOf(e)))
      .finally(() => setBusy(false));
  };

  return (
    <div className="unclear-panel">
      <div className="card__label">{prompt}</div>
      <div className="chips">
        {options.map((o) => (
          <button
            key={String(o.key)}
            type="button"
            className="chip"
            aria-pressed={picked.includes(o.key)}
            onClick={() => toggle(o.key)}
          >
            {o.label}
          </button>
        ))}
      </div>
      <textarea
        className="field field--text unclear-panel__text"
        rows={2}
        maxLength={COMMENT_MAX}
        value={comment}
        placeholder="Комментарий — если хочется пояснить"
        onChange={(e) => setComment(e.target.value)}
      />
      {failed !== null ? <div className="faint">{failed}</div> : null}
      <div className="unclear-panel__actions">
        {had ? (
          <button type="button" className="link link--quiet" disabled={busy} onClick={() => submit([], "")}>
            Снять отметку
          </button>
        ) : null}
        <button type="button" className="btn btn--quiet" disabled={busy} onClick={onClose}>
          Отмена
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || picked.length === 0}
          onClick={() => submit(picked, comment)}
        >
          Отправить
        </button>
      </div>
    </div>
  );
}
